import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { fileNewTabLink } from '../FileArtifactCard';
import { Card } from '../ui';
import { AgentWorkspaceFileItem, useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { SKILLS_ROOT } from '../../../lib/file-browser';
import { FilesBrowser, folderCaption } from './FilesBrowser';
import { SharedWithYou } from './SharedWithYou';
import { useSharedFiles } from '../../../lib/file-shares';
import type { FilesRow } from './files-row';
import { useTranslation } from '../../../lib/i18n';
import type { ReactNode } from 'react';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentName } from '../../../lib/use-agent-name';
import { useBasePath } from '../../../lib/use-base-path';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { designReviewHandoff } from '../../../lib/design-review';
import { useMarkVisited } from '../../../lib/use-last-visited';
import { printDocument } from '../../../lib/doc-print';
import { docViewerKind } from '../../../lib/doc-viewer';
import { SharedFileViewer } from './SharedFileViewer';
import { openFileForRow } from '../../../lib/doc-viewer-presenter';
import { useDockBesideChat } from '../../../lib/chat-file-pane';
import { buildFileActions, type FileActionCallbacks } from '../../../lib/file-actions';
import { effectiveContent } from '../../../lib/file-record';
import browserStyles from './FilesBrowser.module.css';
import { TabConnectHint } from '../category-view/TabConnectHint';
import { FilesEmptyState } from '../category-view/CategoryEmptyState';
import { isHomeEmptyForced } from '../../../lib/dev-home-empty-states';

export const FILES_PAGE = 'agent_workspace_files';
const PAGE = FILES_PAGE;

// The cross-workspace Files view, reachable from "Your Agents": every workspace file, in one browser.
//
// It is a FOLDER BROWSER, not a flat list: the first level is the workspace, and below that whatever folders the
// files' own paths describe (`workspaces/<workspaceId>/<path>` — one path space, see `lib/file-browser.ts`). A
// workspace's own gallery is the SAME `FilesBrowser` rooted at that workspace, which is what makes the two
// screens one screen; this page's own job is just the hero, the data, and how a row addresses a file.
/**
 * The cross-workspace Files view's BODY — the hero-less part of `/files`: the shared-with-you strip and the
 * one `FilesBrowser` rooted at the whole tree. Extracted from `pages/files.tsx` so the map home's desktop
 * "Files" tab renders THE SAME explorer under the map (golden rule: every place that shows files is the same
 * file explorer), rather than a second list that looks like it. The page passes its `PageHeader` as `header`;
 * the map home passes none.
 */
/** `onHome`: hosted by the home's Files category, whose URL is the home's — the browser keeps its folder and
 *  filters in memory instead. */
export function AgentWorkspaceFilesBody({ header, onHome }: { header?: ReactNode; onHome?: boolean }) {
  const { t } = useTranslation();
  // The file a "Shared with you" row was asked to open. `SharedWithYou` has always rendered that button;
  // it was mounted with no handler, so pressing it did nothing until now.
  const [viewingSharedFileId, setViewingSharedFileId] = useState<string | null>(null);
  const router = useRouter();
  const dockBesideChat = useDockBesideChat();
  const basePath = useBasePath();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  // Opening the screen is what clears its "N new" badge on the hub tiles — see `lib/last-visited.ts`.
  useMarkVisited('files');
  const forcedEmpty = !!onHome && isHomeEmptyForced();
  // The home's designed empty state counts what is shared with the reader too, so it waits for that read and
  // stands down while anything is shared or offered (iOS/Android `HomeCategoryEmptyState`).
  const shared = useSharedFiles();
  const nothingShared = forcedEmpty || (shared.status === 'ready' && shared.files.length === 0 && shared.pending.length === 0);
  const state = useAgentWorkspaceFiles();
  useLoadBi('agent-workspace-files', { isLoading: state.status === 'loading', isError: state.status === 'error' });

  const allItems = state.status === 'ready' && !forcedEmpty ? state.items : [];
  // A workspace id means nothing on screen, and the first folder level of this tree IS a workspace.
  // A skill belongs to no Space and contributes no entry — an `undefined` key here would resolve the literal
  // string "undefined" for anything that missed a lookup.
  const workspaceNames = useMemo(
    () =>
      Object.fromEntries(
        allItems.filter((item) => item.workspaceId).map((item) => [item.workspaceId as string, item.workspaceName ?? '']),
      ),
    [allItems],
  );

  /** The per-surface half of a row: a cross-workspace row carries its own workspace slug, so it can name this
   *  file's page, and it says which folder the file came from once the list is flat. */
  const rowFor = (item: AgentWorkspaceFileItem, { narrowed }: { narrowed: boolean }): FilesRow => {
    // A file's own page lives under its Space (`/workspace/<slug>/files/<file>`), so a skill has none to
    // name. Its row falls back to this screen, and the actions needing a real address (Open in new tab)
    // omit themselves rather than render dead — the plain click opens the viewer either way.
    const fileHref = item.workspaceSlug ? fileNewTabLink(item.workspaceSlug, item, basePath) : undefined;
    const href = fileHref ?? `${basePath}/files`;
    return {
      key: `${item.workspaceId ?? SKILLS_ROOT}:${item.name}`,
      item,
      href,
      as: href,
      basePath,
      caption: narrowed ? folderCaption(item, workspaceNames, t) : undefined,
      fileHref,
      workspaceName: item.workspaceName,
    };
  };

  const open = (row: FilesRow) => {
    logPageClickBi({ page_name: PAGE, button_name: 'open_file', answer: row.item.name });
    // EVERY kind opens the viewer, including the ones it cannot preview — it says so and offers Download /
    // Open in new tab, rather than each list improvising its own fallback.
    openFileForRow(row.item, PAGE, row.fileHref, row.workspaceName);
  };

  const actionsFor = (
    row: FilesRow,
    {
      onMove,
      onSendCopy,
      onGrantAccess,
      onShareToGroup,
      onDelete,
    }: { onMove: () => void; onSendCopy: () => void; onGrantAccess?: () => void; onShareToGroup?: () => void; onDelete: () => void },
  ) => {
    const askLink = chatHandoffLink({ kind: 'new', message: t('agentCreations.detail.askMessage', { file: row.item.name }) }, basePath);
    const callbacks: FileActionCallbacks = {
      onView: () => open(row),
      onDiscuss: () => {
        void router.push(askLink.route, askLink.path);
        // Under the chat file pane experiment the file opens beside the chat Discuss starts, where it can dock.
        dockBesideChat(() => openFileForRow(row.item, PAGE, row.fileHref, row.workspaceName, { besideChat: true, handoff: true }));
      },
      // "Get a design review": Discuss's handoff with the design-review request, in the file's Space. The builder
      // offers it only for an eligible file (`lib/design-review.ts`).
      onDesignReview: () => {
        const link = chatHandoffLink(designReviewHandoff(row.item, t), basePath);
        void router.push(link.route, link.path);
        dockBesideChat(() => openFileForRow(row.item, PAGE, row.fileHref, row.workspaceName, { besideChat: true, handoff: true }));
      },
      // The row's own menu has no document on screen to print yet — open the viewer first, then let it settle
      // before printing (for a PDF that means the view's own printer, once its bytes are in).
      onPrint: () => {
        openFileForRow(row.item, PAGE, row.fileHref, row.workspaceName);
        setTimeout(() => printDocument(), 300);
      },
      // Same shape as Print: the editor lives inside the viewer, so opening the file IS opening the editor's
      // host. The viewer's own pencil then takes it the last step, once the document is on screen.
      onEdit: () => openFileForRow(row.item, PAGE, row.fileHref, row.workspaceName, { editing: true }),
      onMove,
      // Hands out a link, a mail draft or a download, and grants nobody anything — the other thing
      // English calls sharing. It stays a NAMED menu item at every width: the row's glyph does not do it,
      // so there is no other way to reach it, and a name is the only form in which the two kinds of
      // sharing can be told apart.
      onSendCopy,
      // GRANTING ACCESS — passed straight through, and `FilesBrowser` supplies it only when narrow (§7
      // dropped it from the WIDE menu, where the row's share glyph is the affordance; a narrow row hides
      // that glyph, so the sheet has to name the action or the access dialog is unreachable — the slot both
      // phones keep it in). This surface asks no width question of its own.
      onGrantAccess,
      // Group chat Phase 7: "Share to group…", offered by `FilesBrowser` behind the flag.
      onShareToGroup,
      // FORGETTING a memory — passed for every row; the builder offers it only under `knowledge/`.
      onDelete,
    };
    const kind = docViewerKind(row.item.name, effectiveContent(row.item));
    return buildFileActions(row.item, kind, callbacks, { context: 'row', t, agentName: agent.short, fileHref: row.fileHref }).map((group) =>
      group.map((action) => ({
        ...action,
        onSelect: () => {
          if (action.key !== 'view') {
            logPageClickBi({ page_name: PAGE, button_name: `file_action_${action.key}`, answer: row.item.name });
          }
          action.onSelect?.();
        },
      })),
    );
  };

  return (
    <>
        {/* The page's own copy of the browser's named size container, so the HERO's gap down to the
            controls (26 on a phone, Figma `15918:426637`) can be asked the same question every other
            fold on this screen is asked. The hero is a sibling of the browser, so no query about the
            browser can reach it, and a `@media` here would be the second threshold this round removed.
            See `.page` in `FilesBrowser.module.css`. */}
        <div className={browserStyles.page}>
        {header}

        {/* What other people have shared with you. Its own section rather than mixed into the browser
            below: those are YOUR files, these are somebody else's, and the difference decides what
            you can do with them and whether they can vanish. Renders nothing at all when there is
            nothing shared — a permanently empty heading is furniture. */}
        {!forcedEmpty && <SharedWithYou onOpen={(file) => setViewingSharedFileId(file.fileId)} />}

        {state.status === 'error' ? (
          <Card disablePadding className={browserStyles.card}>
            <div className={browserStyles.noResults}>{t('workspace.files.error')}</div>
          </Card>
        ) : state.status === 'loading' ? (
          <Card disablePadding className={browserStyles.card}>
            <div className={browserStyles.noResults}>{t('common.loading')}</div>
          </Card>
        ) : (
          <FilesBrowser
            items={allItems}
            root=""
            pageName={PAGE}
            stateInUrl={!onHome}
            title={onHome ? t('categoryView.files.title') : undefined}
            description={onHome ? <TabConnectHint tab="files" fallback={t('categoryView.files.description')} /> : undefined}
            rowFor={rowFor}
            actionsFor={actionsFor}
            onOpen={open}
            onRowMenu={(row) => logPageClickBi({ page_name: PAGE, button_name: 'file_row_menu', answer: row.item.name })}
            onBi={(component) => logPageClickBi({ page_name: PAGE, button_name: component })}
            workspaceNames={workspaceNames}
            emptyCaption={t('files.empty')}
            emptyState={onHome && nothingShared ? <FilesEmptyState pageName={PAGE} /> : undefined}
            // A new file is empty, so there is nothing to read — it opens straight into its editor, which is
            // the only reason anyone creates one.
            onCreated={(file) => {
              logPageClickBi({ page_name: PAGE, button_name: 'file_created', answer: file.name });
              openFileForRow(file, PAGE, undefined, workspaceNames[file.workspaceId ?? ''], { editing: true });
            }}
          />
        )}
        </div>
      {viewingSharedFileId && <SharedFileViewer fileId={viewingSharedFileId} onClose={() => setViewingSharedFileId(null)} />}
    </>
  );
}
